/**
 * Phase 2.1 season titles: the top 3 of the last closed season (SeasonFacet.seasonTitles, stored
 * on-chain at rollover). <SeasonBadge address=.../> renders a small title chip next to a player.
 * Hidden unless VITE_SEASONS_ENABLED=true and the facet is routed; one shared read per page load.
 */
import { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { getReadProvider } from '../utils/contract';
import { CHAIN } from '../config/chain';
import { SEASONS_LIVE } from '../config/onchainFeatures';

const ABI = [
  'function currentSeason() view returns (uint256 id,uint256 start,uint256 end,uint256 timeLeft,bool rolloverDue)',
  'function seasonTitles(uint256) view returns (address[3] top,uint256[3] pts,uint8 winningFaction,bool finalized)',
];

export interface SeasonTitles { season: number; top: string[]; winningFaction: number }
let cache: Promise<SeasonTitles | null> | null = null;

export function loadSeasonTitles(): Promise<SeasonTitles | null> {
  if (!SEASONS_LIVE) return Promise.resolve(null);
  if (!cache) {
    cache = (async () => {
      try {
        const c = new ethers.Contract(CHAIN.gameAddress, ABI, getReadProvider());
        const cur = await c.currentSeason();
        // newest closed season: current-1 normally; if rollover is due, the current one is over
        // but not frozen yet, so show the last frozen one.
        const id = Number(cur.id) - 1;
        if (id < 1) return null;
        const t = await c.seasonTitles(id);
        if (!t.finalized) return null;
        return { season: id, top: (t.top as string[]).map((a) => a.toLowerCase()), winningFaction: Number(t.winningFaction) };
      } catch { return null; }
    })();
  }
  return cache;
}

export function useSeasonTitles() {
  const [t, setT] = useState<SeasonTitles | null>(null);
  useEffect(() => { let dead = false; loadSeasonTitles().then((v) => { if (!dead) setT(v); }); return () => { dead = true; }; }, []);
  return t;
}

const PLACES = [
  { icon: '\u{1F3C6}', label: 'Champion', cls: 'border-yellow-400/50 bg-yellow-400/15 text-yellow-300' },
  { icon: '\u{1F948}', label: '2nd', cls: 'border-slate-300/50 bg-slate-300/15 text-slate-200' },
  { icon: '\u{1F949}', label: '3rd', cls: 'border-orange-400/50 bg-orange-400/15 text-orange-300' },
];

export default function SeasonBadge({ address, titles }: { address?: string | null; titles?: SeasonTitles | null }) {
  const own = useSeasonTitles();
  const t = titles === undefined ? own : titles;
  if (!t || !address) return null;
  const i = t.top.indexOf(address.toLowerCase());
  if (i < 0) return null;
  const p = PLACES[i];
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap ${p.cls}`}
      title={`Season ${t.season} ${p.label}: top ${i + 1} in season points (on-chain title)`}
    >
      {p.icon} S{t.season} {p.label}
    </span>
  );
}
